<template>
	<Row class="page-container-row">
		<Col :span="24" :offset="0" :xxl="{ span: 18, offset: 3 }">
			<PageContainer>
				<Spin :spinning="isLoading">
					<Card>
						<template #title>
							<BaseStationFormHeader :baseStationObj="baseStationObj" :baseStationDetails="baseStationDetails" :messages="messages" />
						</template>
						<Form :model="formState" :label-col="{ span: 8 }" :wrapper-col="{ span: 16 }" :colon="false" labelWrap>
							<FormItem ref="labelField" :label="$t('base-stations-edit.fields.label')"
								v-bind="validateInfos.label">
								<Input v-model:value="formState.label" />
							</FormItem>
							<FormItem v-bind="validateInfos.mntpntInt">
								<template #label>
									<MntpntIntTooltip />
								</template>
								<InputRestricted
									v-model="formState.mntpntInt"
									:maxlength="MNTPNT_INT_MAX_LENGTH"
									:allowedFormatChecker="mountpointChecker"
									:toolTipText="$t('validators.mntpntIntMessage')" />
							</FormItem>
							<AntBlock
								:formState="formState"
								:formSettings="formSettings"
								:baseStationId="baseStationId"
								:baseStationDetails="baseStationDetails"
								:validateInfos="validateInfos"
								:smallScreen="smallScreen"
								@updateFormState="updateFormState"
							/>

							<RadioGroupWithTooltip
								v-model="formState.protocol"
								:options="formSettings.protocols"
								:validateInfo="validateInfos.protocol"
								prefix="base-stations-edit.fields.protocol"
							/>
							
							<div v-if="formSimpleOptions.isProtocolSecureRequired">
								<FormItem :label="$t('base-stations-edit.fields.protocolSecure')"
									v-bind="validateInfos.protocolSecure">
									<RadioGroup v-model:value="formState.protocolSecure" :options="formSimpleOptions.secureProtocolOptions"
										option-type="button" />
								</FormItem>
								<ProtocolNtripHelp v-if="formSimpleOptions.isProtocolSecureHelpVisible" />
							</div>

							<div v-if="smallScreen">
								<LabeledSwitch class="pd-md-vertical" v-model="formState.includeInNear" :rightLabel="$t('base-stations-edit.fields.includeInNear')" />
								<LabeledSwitch v-if="formSettings.allowPppRtk" class="pd-md-vertical" v-model="formState.includeInVrs" :rightLabel="$t('base-stations-edit.fields.includeInVrs')" :aria-label="$t('base-stations-edit.fields.includeInVrs')" />
								<LabeledSwitch class="pd-md-vertical" v-model="formState.dumpCasterInputRawData" :rightLabel="$t('base-stations-edit.fields.dumpRawData')" />
							</div>

							<FormItem
								v-if="!smallScreen"
								class="pd-md-vertical"
								:label="$t('base-stations-edit.fields.includeInNear')"
								v-bind="validateInfos.includeInNear"
								:label-col="{ span: 12, lg: { span: 8 } }"
								:wrapper-col="{ span: 12, lg: { span: 16 } }"
							>
								<LabeledSwitch v-model="formState.includeInNear" />
							</FormItem>
							<FormItem
								v-if="!smallScreen && formSettings.allowPppRtk"
								class="pd-md-vertical"
								:label="$t('base-stations-edit.fields.includeInVrs')"
								v-bind="validateInfos.includeInVrs"
								:label-col="{ span: 12, lg: { span: 8 } }"
								:wrapper-col="{ span: 12, lg: { span: 16 } }"
							>
								<LabeledSwitch v-model="formState.includeInVrs" :aria-label="$t('base-stations-edit.fields.includeInVrs')" />
							</FormItem>
							<FormItem
								v-if="!smallScreen"
								class="pd-md-vertical"
								:label-col="{ span: 12, lg: { span: 8 } }"
								:wrapper-col="{ span: 12, lg: { span: 16 } }"
							>
								<template #label>
									<StoragePathTooltip
										:storagePath="formSettings.storagePath"
										:relativePath="$t('base-stations-edit.tooltips.dumpRawDataTooltip')"
										:label="$t('base-stations-edit.fields.dumpRawData')"
									/>
								</template>
								<LabeledSwitch v-model="formState.dumpCasterInputRawData" />
							</FormItem>

							<SubNetworksBlock
								:formState="formState"
								:formSettings="formSettings"
								:baseStationId="baseStationId"
								:loadBaseStationDetails="loadBaseStationDetails"
								@updateFormState="updateFormState"
							/>

							<template v-if="formSimpleOptions.isHostRequired || formSimpleOptions.isPortRequired || formSimpleOptions.isMntpntSrcRequired || formSimpleOptions.isCredentialsRequired">
								<DividerAutoDirection>
									{{ $t('base-stations-edit.fields.serverDividers.' + (formState.protocol || 'none')) }}
								</DividerAutoDirection>
								<Row>
									<Col span="12" v-if="formSimpleOptions.isHostRequired || formSimpleOptions.isPortRequired">
										<FormItem :label="$t('base-stations-edit.fields.host')" v-bind="validateInfos.host"
											v-if="formSimpleOptions.isHostRequired">
											<InputRestricted v-model="formState.host" :allowedFormatChecker="hostnameChecker"
												:toolTipText="$t('validators.hostnameMessage')" />
										</FormItem>
										<FormItem v-bind="validateInfos.port" v-if="formSimpleOptions.isPortRequired">
											<template #label>
												<PortTooltip :protocolTcpClient="protocolTcpClient" :portMinValue="portMinValue" :portMaxValue="portMaxValue" />
											</template>
											<InputDecimal v-model="formState.port" :allowPoint="false" :range="portRange"
												:minValue="portMinValue" :maxValue="portMaxValue" />
										</FormItem>
										<Row v-if="protocolTcpClient && formSettings.tcpPortError.length > 0" class="pd-vertical">
											<Col span="16" offset="8"><Tag color="pink">{{ formSettings.tcpPortError }}</Tag></Col>
										</Row>
									</Col>
									<Col v-if="formSimpleOptions.isMntpntSrcRequired" span="12">
										<FormItem :label="$t('base-stations-edit.fields.mntpntSrc')" v-bind="validateInfos.mntpntSrc">
											<InputRestricted
												v-model="formState.mntpntSrc"
												:allowedFormatChecker="mountpointChecker"
												:toolTipText="$t('validators.mountpointMessage')"
											/>
										</FormItem>
									</Col>
								</Row>
								<Row v-if="formSimpleOptions.isCredentialsRequired">
									<Col span="12">
										<FormItem :label="$t('base-stations-edit.fields.username')" v-bind="validateInfos.username">
											<InputRestricted v-model="formState.username"
												:allowedFormatChecker="asciiNoColonChecker"
												:toolTipText="$t('validators.asciiNoColonMessage')" />
										</FormItem>
									</Col>
									<Col span="12">
										<FormItem :label="$t('base-stations-edit.fields.password')" v-bind="validateInfos.password">
											<InputRestricted v-model="formState.password"
												:toolTipText="$t('validators.asciiMessage')" />
										</FormItem>
									</Col>
								</Row>
								<Row v-if="formSimpleOptions.isRemoteAddressVisible || formSimpleOptions.isCasterAddressVisible">
									<Col span="24">
										<FormItem :label="$t('base-stations-edit.fields.hostAddress')" :label-col="{ span: 4 }" :wrapper-col="{ span: 20 }">
											<span v-if="formSimpleOptions.isRemoteAddressVisible" class="pd-md-x">
												{{ formSettings.netStatusData.containerIpAddr }}:{{ formState.port }}
											</span>
											<span v-if="formSimpleOptions.isCasterAddressVisible" class="pd-md-x">
												{{ formSettings.netStatusData.containerIpAddr }}:{{ formSettings.netStatusData.casterPort }}
											</span>
										</FormItem>
									</Col>
								</Row>
								<Row v-if="formSimpleOptions.isRequestVisible">
									<Col span="12">
										<FormItem v-bind="validateInfos.request">
											<template #label><RequestTooltip /></template>
											<Textarea v-model:value="formState.request" :rows="3" :placeholder="$t('base-stations-edit.fields.requestPlaceholder')" />
										</FormItem>
									</Col>
								</Row>
							</template>

							<FtpBlock
								:formState="formState"
								:formSettings="formSettings"
								:baseStationId="baseStationId"
								:loadBaseStationDetails="loadBaseStationDetails"
								@updateFormState="updateFormState"
							/>

							<CoordsBlock
								:formState="formState"
								:baseStationDetails="baseStationDetails"
								:validateInfos="validateInfos"
								:formSettings="formSettings"
								@updateFormState="updateFormState"
							/>
							<CoordsCorrections
								:formState="formState"
								:baseStationDetails="baseStationDetails"
								:allowPaas="licenseStore.allowPaas"
								:loadBaseStationDetails="onSubmitHook"
								:resetChangedState="resetChangedState"
								:formsChanged="formsChanged"
							/>
							<RecommendedCoordsBlock
								:baseStationDetails="baseStationDetails"
								:loadBaseStationDetails="onSubmitHook"
								:resetChangedState="resetChangedState"
								:formsChanged="formsChanged"
							/>

							<Row style="margin-top: 24px">
								<Col span="24" :lg="{ span: 12 }">
									<FormItem :label="$t('base-stations-edit.fields.info')" v-bind="validateInfos.info">
										<Textarea v-model:value="formState.info" :rows="3" />
									</FormItem>
								</Col>
							</Row>
							
							<BaselinesBlock v-if="baseStationObj != null && licenseStore.allowBaselines" :baseStationObj="baseStationObj" />
						</Form>
						<template #actions>
							<Space v-if="!baseStationDetails.deleted">
								<Button v-if="baseStationObj !== null" type="primary" @click.prevent="onSubmit(false)" :loading="isSubmitting">
									{{ messages.submitButtonLabel }}
								</Button>
								<Button type="primary" @click.prevent="onSubmit(true)" :loading="isSubmitting">
									{{ messages.submitAndCloseButtonLabel }}
								</Button>
								<Button v-if="baseStationObj !== null" @click.prevent="onDuplicate">
									{{ $t('base-stations-edit.duplicateButtonLabel') }}
								</Button>
							</Space>
							<ChangeStatusActionBar
								v-if="baseStationObj !== null && isBaseStationChangeAllowed"
								:target="baseStationObj"
								:store="baseStationsStore"
								mode="baseStation"
								@delete="resetBaseStationDetails"
								@restore="resetBaseStationDetails"
							/>
						</template>
					</Card>
				</Spin>
				<VerticalLayout v-if="licenseStore.allowPaas && baseStationId != 0" class="pd-vertical">
					<ManualPaasForm :baseStationId="baseStationId" />
					<PaasCoordsChart
						:pinnedMountpoint="baseStationDetails.mntpntInt"
						:minDate="minDate"
						:maxDate="maxDate"
						@corrected="loadBaseStationDetails"
					/>
				</VerticalLayout>
				<div v-if="baseStationId != 0" class="pd-vertical">
					<TypographyTitle :level="3" class="pd-lg-x">{{ $t('breaks-gantt.header') }}</TypographyTitle>
					<BreaksGanttPage
						:initMountpointSelected="baseStationDetails.mntpntInt"
						:showDescription="false"
						:minDate="minDate"
						:maxDate="maxDate"
					/>
				</div>
				<a name="gnss-visibility" />
				<div v-if="baseStationId != 0" class="pd-vertical">
					<GnssVisibilityChartGroupBaseStationStatsPage
						:initMountpointSelected="baseStationDetails.mntpntInt"
						:showDateRangeChooser="false"
						:minDate="minDate"
						:maxDate="maxDate"
					/>
					<GnssVisibilitySignalsTable :baseStationId="baseStationId" />
				</div>
				<div v-if="baseStationId != 0" class="pd-md-vertical">
					<TypographyTitle :level="3" class="pd-lg-x">{{ $t('net-info-table.header') }}</TypographyTitle>
					<NetInfoTable :baseStationId="baseStationId" />
				</div>
			</PageContainer>
		</Col>
	</Row>
	<RouteLeaveModal
		:visible="modalVisible"
		:confirm="modalOk"
		:cancel="modalCancel"
		:submit="onSubmitRouteLeaveModal"
		:loading="isSubmittingRouteLeaveModal"
		:submitText="messages.submitAndCloseButtonLabel"
	/>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, toRaw } from 'vue';
import { useI18n } from 'vue-i18n';

import {
	Form, FormItem, Input, Button, Row, Col, Card, Space, TypographyTitle,
	Spin, RadioGroup, Textarea, Tag, message
} from 'ant-design-vue';

import { useRoute, useRouter } from 'vue-router';

import { useBaseStationsStore } from '@/stores/baseStations';
import { useLicenseStore } from '@/stores/licenseStore';
import { useUserStore } from '@/stores/user';

import { getChartDays } from '@/components/addBaseStation/chartDays';

import RouteLeaveModal from '@/components/common/RouteLeaveModal';
import { useRouteLeaveModal } from '@/components/common/useRouteLeaveModal';

import Utils from '@/api/Utils';
import Validators from '@/api/Validators';

import PageContainer from '@/components/common/PageContainer';
import InputDecimal from '@/components/common/InputDecimal';
import InputRestricted from '@/components/common/InputRestricted';
import LabeledSwitch from '@/components/common/LabeledSwitch';
import VerticalLayout from '@/components/common/VerticalLayout';

import BaseStationFormHeader from '@/components/addBaseStation/BaseStationFormHeader';
import ManualPaasForm from '@/components/addBaseStation/ManualPaasForm';
import AntBlock from '@/components/addBaseStation/AntBlock';
import SubNetworksBlock from '@/components/addBaseStation/SubNetworksBlock';
import FtpBlock from '@/components/addBaseStation/FtpBlock';
import CoordsBlock from '@/components/addBaseStation/CoordsBlock';
import CoordsCorrections from '@/components/addBaseStation/CoordsCorrections';
import RecommendedCoordsBlock from '@/components/addBaseStation/RecommendedCoordsBlock';

import MntpntIntTooltip from '@/components/addBaseStation/tooltips/MntpntIntTooltip';
import PortTooltip from '@/components/addBaseStation/tooltips/PortTooltip';
import RequestTooltip from '@/components/addBaseStation/tooltips/RequestTooltip';
import ProtocolNtripHelp from '@/components/addBaseStation/ProtocolNtripHelp';
import BaselinesBlock from '@/components/addBaseStation/BaselinesBlock';
import GnssVisibilitySignalsTable from '@/components/addBaseStation/GnssVisibilitySignalsTable';
import NetInfoTable from '@/components/addBaseStation/NetInfoTable';

import DividerAutoDirection from '@/components/common/DividerAutoDirection.vue';
import StoragePathTooltip from '@/components/common/StoragePathTooltip.vue';

import { useBaseStationData } from '@/components/addBaseStation/useBaseStationData';
import { useMessages } from '@/components/addBaseStation/useMessages';
import { useFormSimpleOptions } from '@/components/addBaseStation/useFormSimpleOptions';
import { useFormSettings } from '@/components/addBaseStation/useFormSettings';
import { useFormSubmit } from '@/components/addBaseStation/useFormSubmit';
import { useSmallScreen } from '@/utils/useSmallScreen';
import { saveDuplicateBaseStationTemplate, readDuplicateBaseStationTemplate } from '@/utils/baseStationDuplicateTemplate';

import PaasCoordsChart from '@/components/paas/PaasCoordsChart';

import BreaksGanttPage from '@/components/stats/BreaksGanttPage';
import GnssVisibilityChartGroupBaseStationStatsPage from '@/components/stats/GnssVisibilityChartGroupBaseStationStatsPage';

import RadioGroupWithTooltip from '@/components/forms/RadioGroupWithTooltip';

import ChangeStatusActionBar from '@/components/baseStations/ChangeStatusActionBar';

export default {
	components: {
		Form, FormItem, Input, Button, Row, Col, Card, Spin, Space, TypographyTitle, Tag, Textarea,
		InputDecimal, InputRestricted, LabeledSwitch, RadioGroup, PageContainer, ChangeStatusActionBar,
		BaseStationFormHeader, AntBlock, SubNetworksBlock, FtpBlock, CoordsBlock, CoordsCorrections, PaasCoordsChart,
		BreaksGanttPage, GnssVisibilityChartGroupBaseStationStatsPage, GnssVisibilitySignalsTable, NetInfoTable,
		RouteLeaveModal, ManualPaasForm, RadioGroupWithTooltip,
		MntpntIntTooltip, StoragePathTooltip, PortTooltip, RequestTooltip, DividerAutoDirection,
		ProtocolNtripHelp, VerticalLayout, BaselinesBlock, RecommendedCoordsBlock,
	},
	setup() {
		const { t } = useI18n();
		const router = useRouter();
		const baseStationId = ref(0);
		const { messages, setBaseStationLabel, setEditModeMessages } = useMessages('add-base-station', 'base-stations-edit');
		/*
		Надо использовать отдельно baseStationObj и baseStationDetails, так как baseStationObj -- это computed,
		связанный со store. При изменении статуса БС, надо получить состояние из store.
		baseStationDetails -- это просто dict с уточненными данными
		*/
		const {
			formState,
			baseStationObj,
			baseStationDetails,
			isLoadingDetails,
			updateFormState,
			loadBaseStationDetails,
		} = useBaseStationData(baseStationId, setBaseStationLabel);

		const { formChangedStatuses, modalVisible, formsChanged, modalOk, modalCancel } = useRouteLeaveModal();
		const { enableWatch, pauseWatch, resetChangedState } = Validators.useWatchFormChanges(formState, formChangedStatuses, 'addBaseStationForm');
		const resetBaseStationDetails = () => {
			resetChangedState();
			pauseWatch();
			loadBaseStationDetails();
		}

		const route = useRoute();
		if (route.name === 'base-stations-edit' && Utils.isObjectWithData(route.params)) {
			baseStationId.value = Number(route.params.id);
			setEditModeMessages();
			loadBaseStationDetails();
		} else {
			if (route.name === 'duplicate-base-station') {
				const duplicateFormData = readDuplicateBaseStationTemplate();
				if (Utils.isObjectWithData(duplicateFormData)) {
					Object.assign(formState, duplicateFormData);
				}
			}
			formState.ftpPassword = Utils.getRandomString(10);
		}

		const { isLoadingSettings, formSettings } = useFormSettings(formState);
		const isLoading = computed(() => isLoadingDetails.value || isLoadingSettings.value);
		watch(isLoading, value => {
			if (!value) {
				nextTick(enableWatch);
			}
		})

		const {
			validate,
			validateInfos,
			protocolTcpClient,
			portMinValue,
			portMaxValue,
			portRange,
		} = Validators.useAddBaseStationFormRules(formState, formSettings);

		const baseStationsStore = useBaseStationsStore();
		const licenseStore = useLicenseStore();
		const { minDate, maxDate } = getChartDays();
		const onSubmitHook = () => {
			if (baseStationId.value > 0) {
				pauseWatch();
				loadBaseStationDetails();
			}
		}
		const { isSubmitting, onSubmit } = useFormSubmit(validate, baseStationId, formState, validateInfos, messages, resetChangedState, onSubmitHook);
		const onErrorHookRouteLeaveModal = (isError) => {
			if (isError) {
				message.error(t('base-stations-edit.messages.error'));
			}
			modalCancel();
		}
		const {
			isSubmitting: isSubmittingRouteLeaveModal,
			onSubmit: onSubmitRouteLeaveModal,
		} = useFormSubmit(validate, baseStationId, formState, validateInfos, messages, resetChangedState, modalOk, onErrorHookRouteLeaveModal);

		const labelField = ref();
		onMounted(() => {
			nextTick(() => {
				if (labelField.value?.focus) {
					labelField.value.focus();
				}
				if (route.hash) {
					// implements scroll to anchor. For example, #gnss-visibility
					location.hash = route.hash;
				}
			});
		});
		const user = useUserStore();
		const isBaseStationChangeAllowed = computed(() => user.isBaseStationChangeAllowed);
		const onDuplicate = () => {
			let newData = {...toRaw(formState)};
			delete newData.id;
			saveDuplicateBaseStationTemplate(newData);
			resetChangedState();
			router.push({ name: 'duplicate-base-station' });
		};

		const { smallScreen } = useSmallScreen();
		return {
			baseStationsStore,
			licenseStore,
			formState,
			formSettings,
			validateInfos,
			protocolTcpClient,
			portMinValue,
			portMaxValue,
			portRange,
			isLoading,
			baseStationId,
			baseStationObj,
			baseStationDetails,
			isBaseStationChangeAllowed,
			messages,
			updateFormState,
			loadBaseStationDetails,
			resetBaseStationDetails,
			onSubmitHook,
			resetChangedState,
			MNTPNT_INT_MAX_LENGTH: Validators.MNTPNT_INT_MAX_LENGTH,
			alphaNumericChecker: Validators.alphaNumericChecker,
			mountpointChecker: Validators.mountpointChecker,
			asciiNoColonChecker: Validators.asciiNoColonChecker,
			hostnameChecker: Validators.hostnameChecker,
			formSimpleOptions: useFormSimpleOptions(formState),

			modalVisible,
			formsChanged,
			modalOk,
			modalCancel,

			isSubmitting,
			onSubmit,
			onDuplicate,

			isSubmittingRouteLeaveModal,
			onSubmitRouteLeaveModal,

			minDate, maxDate,
			labelField,
			smallScreen,
		};
	}
}
</script>

<style>
.ant-typography.tooltip-code code {
	color: black;
	background-color: #eee;
}
</style>
